import React, { useMemo } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  ListItem,
  ListItemText,
  List,
  IconButton,
  Badge,
  Tooltip,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  BrandBatchSettings,
  BrandBatchSettingsWithPhrases,
  ClientJobStatus,
  JobStatus,
  User,
  colorByStatus,
  statusToLabel,
} from "../../pages/playground/types";
import {
  Phrase,
  PhraseType,
} from "../../api/phrasebook";
import { getGroupById, toPhraseLimits } from "../../utils/content-groups";
import {
  processPhrasesValidation,
  getValidationIssues,
} from "../editor/limits-processor";
import { Warning } from "@mui/icons-material";

const EditorStatus: React.FC<{
  status: JobStatus | undefined;
  isUpdatingStatus?: boolean;
  onStatusModified: (newStatus: JobStatus) => void;
  phrases: Phrase[];
  settings: BrandBatchSettings | null;
  wordsToAvoid?: string[];
  user?: User;
  isEditor?: boolean;
  isReviewer?: boolean;
}> = ({
  status,
  isUpdatingStatus = false,
  onStatusModified,
  phrases,
  settings,
  wordsToAvoid,
  user,
  isEditor,
  isReviewer,
}) => {
  // Process validation limits
  const { validationIssues } = useMemo(() => {
    // Split phrases by type for validation
    const phrasesByType: { [key: string]: Phrase[] } = {};

    phrases.forEach((phrase) => {
      if (phrase.type && !phrasesByType[phrase.type]) {
        phrasesByType[phrase.type] = [];
      }
      if (phrase.type) {
        phrasesByType[phrase.type].push(phrase);
      }
    });

    // For each phrase type, validate against the corresponding settings
    const resultsByType: {
      [key: string]: ReturnType<typeof processPhrasesValidation>;
    } = {};

    const allIssues: string[] = [];

    // Only process if settings exist
    if (settings) {
      Object.entries(phrasesByType).forEach(([typeKey, typePhrases]) => {
        const phraseType = typeKey as PhraseType;
        // Get the content group for this phrase type
        const group = getGroupById(settings, phraseType);
        const typeSettings = group
          ? toPhraseLimits(group) as BrandBatchSettingsWithPhrases
          : undefined;

        if (typeSettings && typeSettings.enabled) {
          // Validate this phrase type against its settings
          const result = processPhrasesValidation(
            typePhrases,
            typeSettings,
            wordsToAvoid
          );

          resultsByType[phraseType] = result;

          // Get validation issues for this type
          const typeIssues = getValidationIssues(
            result,
            typeSettings,
            phraseType,
            settings || undefined
          );

          allIssues.push(...typeIssues);
        }
      });
    }

    return {
      validationIssues: allIssues,
    };
  }, [phrases, settings, wordsToAvoid]);

  // Determine if there are validation errors that would prevent moving beyond IN_PROGRESS
  const hasBlockingValidationIssues = useMemo(() => {
    // If there are validation issues and we're trying to move beyond IN_PROGRESS
    return validationIssues.length > 0;
  }, [validationIssues]);

  if (!status) return null;

  const isAdminOrManager = user?.isAdmin || user?.isManager;

  const showEditorAction = isEditor && status === JobStatus.IN_PROGRESS;
  const showReviewerAction = isReviewer && status === JobStatus.TO_BE_REVIEWED;

  let availableStatuses: JobStatus[] | ClientJobStatus[] =
    Object.values(JobStatus);

  if (user?.isClientRelated) {
    availableStatuses = Object.values(ClientJobStatus);
  }

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {isUpdatingStatus && (
        <CircularProgress size={20} sx={{ mr: 1 }} />
      )}
      <FormControl
        size="small"
        sx={{
          width: { xs: "100%", sm: "auto" },
        }}
        disabled={isUpdatingStatus}
      >
        <InputLabel id="status-label">Status</InputLabel>
        <Select
          labelId="status-label"
          value={status}
          label="Status"
          disabled={isUpdatingStatus}
          onChange={(e) => onStatusModified(e.target.value as JobStatus)}
        >
          {availableStatuses.map((statusOption) => {
            let isDisabled = false;

            if (
              statusOption === JobStatus.TO_BE_STARTED &&
              [
                JobStatus.TO_BE_REVIEWED,
                JobStatus.TO_BE_REWORKED,
                JobStatus.DELIVERED,
                JobStatus.COMPLETED,
              ].includes(status)
            ) {
              isDisabled = true;
            }

            if (
              statusOption === JobStatus.IN_PROGRESS &&
              [JobStatus.DELIVERED, JobStatus.COMPLETED].includes(status)
            ) {
              isDisabled = true;
            }

            if (
              (statusOption === JobStatus.TO_BE_REVIEWED && !isEditor) ||
              [JobStatus.DELIVERED, JobStatus.COMPLETED].includes(status)
            ) {
              isDisabled = true;
            }

            if (statusOption === JobStatus.TO_BE_REWORKED) {
              isDisabled = true;
            }

            if (statusOption === JobStatus.DELIVERED && !isReviewer) {
              isDisabled = true;
            }

            if (isAdminOrManager) {
              isDisabled = false;
            }

            if (statusOption === JobStatus.COMPLETED) {
              isDisabled = true;

              if (isAdminOrManager || isReviewer) {
                isDisabled = false;
              }
            }

            if (user?.isClientRelated) {
              isDisabled = false;
            }

            return (
              <MenuItem
                key={statusOption}
                value={statusOption}
                disabled={isDisabled}
                sx={{
                  color: colorByStatus[statusOption],
                }}
              >
                {statusToLabel(statusOption)}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {showEditorAction && (
        <Tooltip title="Mark the job as ready for review and move to the next job to edit.">
          <Button
            variant="contained"
            color={hasBlockingValidationIssues ? "warning" : "success"}
            size="small"
            disabled={isUpdatingStatus}
            onClick={() => onStatusModified(JobStatus.TO_BE_REVIEWED)}
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
            }}
          >
            Move to
            <br />
            To be reviewed
          </Button>
        </Tooltip>
      )}

      {showReviewerAction && (
        <Tooltip title="Mark the job as delivered (the client will be able to view it) and move to the next job to review.">
          <Button
            variant="contained"
            color={hasBlockingValidationIssues ? "warning" : "success"}
            size="small"
            disabled={isUpdatingStatus}
            onClick={() => onStatusModified(JobStatus.DELIVERED)}
            sx={{ textTransform: "none", whiteSpace: "nowrap" }}
          >
            Move to
            <br />
            Delivered
          </Button>
        </Tooltip>
      )}

      {hasBlockingValidationIssues && (
        <Tooltip
          slotProps={{
            tooltip: {
              sx: {
                bgcolor: "white",
                color: "text.primary",
                boxShadow: 3,
                borderRadius: 1,
                p: 2,
                maxWidth: "500px",
                overflow: "auto",
                whiteSpace: "pre-line",
                wordBreak: "break-word",
              },
            },
            arrow: {
              sx: {
                color: "white",
              },
            },
          }}
          title={
            <Box sx={{ p: 0 }}>
              {validationIssues.length > 0 ? (
                <List dense>
                  {validationIssues.map((issue: string, index: number) => (
                    <ListItem
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        py: 0.5,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          width: "6px",
                          height: "6px",
                          bgcolor: "warning.main",
                          borderRadius: "50%",
                          mr: 1.5,
                          mt: 1.2,
                        }}
                      />
                      <ListItemText
                        primary={issue}
                        primaryTypographyProps={{
                          variant: "body2",
                          sx: {
                            fontWeight: "medium",
                            color: "text.primary",
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" sx={{ py: 1 }}>
                  No specific validation problems detected, but the general
                  requisites are not matched.
                </Typography>
              )}
            </Box>
          }
          placement="bottom-start"
          arrow
        >
          <IconButton size="small" sx={{ mr: 1, color: "warning.main" }}>
            <Badge badgeContent={validationIssues.length} color="warning">
              <Warning />
            </Badge>
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default EditorStatus;
